import type { UserRole } from '@mm/shared';
import { NavLink } from 'react-router-dom';
import { useAuth } from '../auth/AuthContext';
import { cn } from '../lib/cn';
import { IconHome, IconUsers } from '../ui/icons';

type NavItem = {
  to: string;
  label: string;
  num: string;
  Icon: typeof IconHome;
  end: boolean;
};

const TUTOR_NAV: NavItem[] = [
  { to: '/app', label: 'Главная', num: 'I', Icon: IconHome, end: true },
  { to: '/app/students', label: 'Ученики', num: 'II', Icon: IconUsers, end: false },
];

const STUDENT_NAV: NavItem[] = [
  { to: '/app/my', label: 'Главная', num: 'I', Icon: IconHome, end: true },
];

function navForRole(role: UserRole | undefined): NavItem[] {
  if (role === 'STUDENT') return STUDENT_NAV;
  return TUTOR_NAV;
}

function sectionLabelForRole(role: UserRole | undefined): string {
  if (role === 'STUDENT') return 'Мой класс';
  return 'Админ';
}

function subtitleForRole(role: UserRole | undefined): string {
  if (role === 'STUDENT') return 'Ученик';
  return 'Репетитор';
}

function initials(name: string | undefined): string {
  if (!name) return '•';
  const parts = name.trim().split(/\s+/);
  const a = parts[0]?.[0] ?? '';
  const b = parts[1]?.[0] ?? '';
  return (a + b).toUpperCase();
}

export function Sidebar() {
  const { user } = useAuth();
  const nav = navForRole(user?.role);

  return (
    <aside className="hidden md:flex w-[212px] shrink-0 flex-col gap-3 border-r border-border bg-panel px-2.5 py-3.5">
      <div className="px-2.5 pt-1.5 pb-3 flex items-baseline gap-2">
        <span className="font-display text-[18px] font-semibold text-fg" style={{ fontVariationSettings: '"opsz" 48, "SOFT" 20' }}>
          board
        </span>
        <span className="text-[10px] uppercase tracking-[0.12em] text-muted">matemasha</span>
      </div>

      <div className="font-caps text-[11px] uppercase tracking-[0.12em] text-muted px-2.5 pt-1 pb-0.5">
        {sectionLabelForRole(user?.role)}
      </div>

      <nav className="flex flex-col gap-0.5">
        {nav.map(({ to, label, num, Icon, end }) => (
          <NavLink
            key={to}
            to={to}
            end={end}
            className={({ isActive }) =>
              cn(
                'relative flex items-center gap-2 rounded px-2.5 py-1.5 text-[13px]',
                isActive
                  ? 'bg-accent-soft text-accent font-medium before:absolute before:left-0 before:top-1.5 before:bottom-1.5 before:w-0.5 before:bg-accent before:rounded'
                  : 'text-fg hover:bg-row-hover',
              )
            }
          >
            {({ isActive }) => (
              <>
                <span
                  className={cn(
                    'font-display text-[10px] w-3.5',
                    isActive ? 'text-accent' : 'text-muted',
                  )}
                  style={{ fontVariationSettings: '"opsz" 20' }}
                >
                  {num}
                </span>
                <Icon size={14} className="opacity-80" />
                <span>{label}</span>
              </>
            )}
          </NavLink>
        ))}
      </nav>

      <button
        type="button"
        className="mt-auto flex items-center gap-2.5 rounded px-2 py-2 text-left border-t border-border hover:bg-row-hover transition-colors"
      >
        <span className="grid h-6 w-6 place-items-center rounded bg-fg text-bg font-display text-[11px] font-semibold" style={{ fontVariationSettings: '"opsz" 20' }}>
          {initials(user?.name)}
        </span>
        <span className="min-w-0">
          <span className="block text-[12px] font-medium leading-tight truncate text-fg">
            {user?.name ?? 'Гость'}
          </span>
          <span className="block text-[10px] text-muted">{subtitleForRole(user?.role)}</span>
        </span>
      </button>
    </aside>
  );
}
